import clsx from 'clsx';
import { PaymentStatusType } from '@/constants/paymentStatuses';
import { Button, Countdown, MonitoredBy } from '@/components/common';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useGetBuyTxn } from '@/api/queryHooks/useBuyCryptoController';
import { formatDateAndTime } from '@/utils/functions';
import { Spinner } from '@/components/common/Spinner';
import { useBuyCrypto } from '@/store/buyCrypto';
import { shallow } from 'zustand/shallow';
import { ConfirmationPopupWithTrigger } from '@/components/common/ConfirmationPopupWithTrigger';
import { CryptoCurrency } from '@/api/common.types';
import { Icon } from '../../../../components/common/Icon';
import processing from '../../../../public/animations/payment-processing.json';
import countdown from '../../../../public/animations/payment-countdown.json';
import completed from '../../../../public/animations/payment-completed.json';
import styles from './WaitingPaymentLoader.module.css';

interface PaymentLoaderProps {
  status: PaymentStatusType;
  className?: string;
  retryTx: () => void;
  isUnderPaid?: boolean;
  isCancelBuyTxnLoading?: boolean;
  cancelTxn: () => void;
  onCounterComplete?: () => void;
}

export const WaitingPaymentLoader = ({
  status,
  className,
  retryTx,
  isUnderPaid,
  cancelTxn,
  isCancelBuyTxnLoading,
  onCounterComplete,
}: PaymentLoaderProps) => {
  const { isDesktop } = useMediaQuery();
  const buyState = useBuyCrypto((state) => state, shallow);
  const { txData } = buyState;
  const { data: getTransactionData } = useGetBuyTxn([{ id: txData?.id }, { enabled: false, onSuccess(data) {} }]);

  const counterDuration = 300;

  switch (status) {
    case PaymentStatusType.Locked:
    case PaymentStatusType.Expired:
      return (
        <div className={clsx(styles.paymentLoader, className)}>
          <div className={styles.paymentInner}>
            <div className={styles.countdownWrapper}>
              <Countdown duration={counterDuration} status={status} onComplete={onCounterComplete} />
            </div>
            {isDesktop && (
              <div className={styles.info}>
                <p className={styles.title}>
                  {PaymentStatusType.Locked ? 'Your rate is locked.' : 'Your rate had expired.'}
                </p>
                {PaymentStatusType.Locked ? (
                  <p className={styles.description}>We&apos;re waiting for your payment now. Time remaining:</p>
                ) : (
                  <Button variants="subtle" size="mediumSubtle" icon="retry" onPress={retryTx}>
                    Refresh rate
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className={clsx(styles.divider, styles.actionButtonsWrapper)}>
            <div className={styles.cancelButtonWrapper}>
              <ConfirmationPopupWithTrigger
                triggerButtonProps={{
                  variants: 'subtleWarning',
                  size: 'mediumSubtle',
                  children: 'Cancel Transaction',
                }}
                onSubmit={cancelTxn}
                isLoading={isCancelBuyTxnLoading}
              />
            </div>

            <Button variants="secondary" onPress={retryTx} className={clsx(styles.button, styles.restartButton)}>
              Restart Transaction
            </Button>
          </div>
          {isDesktop && <MonitoredBy className={styles.divider} descriptionClassName={styles.monitoredByDescription} />}
        </div>
      );
    case PaymentStatusType.Replaced:
    case PaymentStatusType.Completed:
      return (
        <div className={clsx(styles.paymentLoader, className)}>
          <Spinner animationData={completed} side={isDesktop ? 48 : 60} />
          <p className={styles.resultTitle}>Payment Completed</p>
          <div className={styles.resultRow}>
            <p className={styles.data}>Thanks for your purchase!</p>
          </div>
          <div className={clsx(styles.resultRow, styles.resultRowWithSeparator)}>
            {isDesktop && <Icon name="check" size={22} className={styles.completedIcon} />}
            <div className={styles.completed}>
              <span>Receiving wallet address:</span>
              <p className={styles.completedAddress}>{txData?.transferAddress}</p>
            </div>
          </div>
          {isUnderPaid && (
            <div className={clsx(styles.UnderpaidResultRow, styles.resultRowWithSeparator)}>
              {isDesktop && <Icon name="warning" size={22} className={styles.warningIcon} />}
              <span className={styles.warning}>
                The amount of AUD that was transferred was lower than expected. Rest assured we still obtained the best
                rate for you. Please expect to see a lower amount of crypto when the transaction is complete.
              </span>
            </div>
          )}
        </div>
      );

    default: {
      const { date, time } = formatDateAndTime(getTransactionData?.updated);
      return (
        <div className={clsx(styles.paymentLoader, className)}>
          <Spinner animationData={processing} side={isDesktop ? 48 : 60} />
          <p className={styles.resultTitle}>Payment Processing</p>
          <div className={styles.resultRow}>
            <p className={styles.data}>{date}</p>
            <p className={styles.time}>{time}</p>
          </div>
          <div className={styles.UnderpaidResultRowWrapper}>
            {isUnderPaid && (
              <div className={clsx(styles.UnderpaidResultRow, styles.resultRowWithSeparator)}>
                {isDesktop && <Icon name="warning" size={22} className={styles.warningIcon} />}
                <span className={styles.warning}>
                  The amount of AUD that was transferred was lower than expected. Rest assured we still obtained the
                  best rate for you. Please expect to see a lower amount of crypto when the transaction is complete.
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
  }
};
